import { locateProxy } from '#~/agent/utils.js';
import { useRatePref, useTempoPref } from '#~/control/movement/hooks.js';
import { Facet, Range, Tempo as M_Tempo } from '#~/control/movement/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { Section } from '#~/room/components/common/section/index.js';
import { Controls as DeviceSettingsControls } from '#~/room/components/image/settings/index.js';
import { DataSTag, Sel } from '#~/stag/index.js';
// Import { Ramp } from './ramp.jsx';
import { Rate } from './rate.jsx';
import { Tempo } from './tempo.jsx';
import type { Props } from './types.js';
import { toAccessor } from '@studio/react-utils';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { twMerge } from 'cn';

/** `<Speed>` component. */
export const Speed = ({ deviceId, facet, ...props }: Props): ReactElement => {
  const tempo = toAccessor(useTempoPref(deviceId, facet));
  const rate = toAccessor(useRatePref(deviceId, facet));

  const { enabled: showVariableSpeedSlider } = useFeatureFlag(
    PostHogFeatureFlag.VARIABLE_SPEED_CONTROL_FEATURE,
    { defaultValue: false }
  );

  const proxy = locateProxy(deviceId);

  const capabilities = proxy?.state.status?.device[deviceId]?.capabilities;

  const isPanTiltDisabled =
    facet === Facet.PanTilt &&
    !capabilities?.movement?.vectors?.pan?.enabled &&
    !capabilities?.movement?.vectors?.tilt?.enabled;

  const isZoomDisabled = facet === Facet.Zoom && !capabilities?.movement?.vectors?.zoom?.enabled;

  const shouldRender = !(isPanTiltDisabled || isZoomDisabled);

  // Auto-set speed to max when variable mode is selected and feature flag is disabled
  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    // Only force max speed when feature flag is disabled
    // When feature flag is enabled, allow user to adjust the slider
    if (tempo.value === M_Tempo.Variable && !showVariableSpeedSlider) {
      if (rate.value.value !== Range.max) {
        rate.setValue({ ...rate.value, value: Range.max });
      }
    }
  }, [tempo.value, rate, facet, shouldRender, showVariableSpeedSlider]);

  if (!shouldRender) {
    return <></>;
  }

  // Determine if rate slider should be shown
  const shouldShowRateSlider = tempo.value === M_Tempo.Fixed || showVariableSpeedSlider;

  // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — STag
  // attribute on the Pan & Tilt / Zoom Section root lets HIL e2e tests
  // locate the section without role/text selectors.
  return (
    <Section
      label={facet === Facet.PanTilt ? 'Pan & Tilt' : 'Zoom'}
      slug={facet}
      {...DataSTag(
        Sel.MainRoomRightTrayControls,
        facet === Facet.PanTilt ? 'pan-tilt-section' : 'zoom-section'
      )}
      {...props}
    >
      <div className={twMerge('flex flex-col')}>
        <Tempo className='mb-8' tempo={tempo} />

        {/* label needs to be passed for now due to props type mismatch */}
        {shouldShowRateSlider && <Rate facet={facet} label={''} rate={rate} />}

        {facet === Facet.PanTilt && (
          <DeviceSettingsControls
            deviceId={deviceId}
            sectionType={DeviceRegistryInterfaceSectionType.PAN_TILT_CONTROL}
            variant='inline'
          />
        )}

        {/* <Ramp deviceId={deviceId} facet={facet} /> */}
      </div>
    </Section>
  );
};
